import { Clinician, Sex, UserContact } from './clinician';
import { PaginatedApiResponse } from './common';
import { Order, OrderStatus, ToplineResult } from './order';
import { IUsername } from './user';

export enum ClinicianReviewStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  INELIGIBLE = 'INELIGIBLE',
  IN_REVIEW = 'IN_REVIEW',
}

export enum RerequisitionType {
  NEW_ORDER = 'NEW_ORDER',
  REANALYSIS = 'REANALYSIS',
  METHYLATION_REFLEX = 'METHYLATION_REFLEX',
}

/* --------------------------------------------------------------------------
 * Relations
 * -------------------------------------------------------------------------- */
export interface ClinicalSummaryLetter {
  completed_on: string;
  description: string;
  mimetype: string;
  uri: string;
}

export interface RequisitionSubjectDemoGraphics {
  dob: string; // YYYY-MM-DD
  sex: Sex;
}

export interface RequisitionSubject {
  name: IUsername;
  demographics: RequisitionSubjectDemoGraphics;
  contact_info: UserContact[];
}

export interface RequisitionVariantFinding {
  enum: string;
  title: string;
}

export interface RequisitionFamilyVariantTest {
  source_requisition?: string;
  source_relationship?: string;
  variants?: RequisitionVariantFinding[];
}

export interface RequisitionSampleRequest {
  type?: string;
  send_kit?: boolean;
  send_kit_address?: UserContact;
}

export interface RequisitionConsentGrant {
  consent_uri: string;
  name: string;
  granted_on: string;
  valid_until?: string;
}

export interface RequisitionInsurance {
  company: string;
  phone?: string;
  member_id: string;
  group_number?: string;
  prior_auth_number?: string;
  relationship_to_policyholder: string;
  relationship_to_policyholder_other?: string;
  epsdt_coverage?: {
    medicaid_plan?: string;
    medicaid_number?: string;
    attestations?: string[];
  };
}

export interface RequisitionBilling {
  billing_type: 'INSTITUTIONAL' | 'INSURANCE' | 'SELF';
  billing_code?: string;
  insurance?: RequisitionInsurance[];
  promo_code?: string;
}

export interface RequisitionOrderObservation {
  observation_type: string;
  value: unknown;
}

export interface RequisitionFamilyMember {
  requisition_id?: string;
  relationship_to_proband?: string;
  subject?: RequisitionSubject & { ancestry?: string[] };
  remote_mrn?: string;
  sample_request?: RequisitionSampleRequest;
  secondary_findings_opt_out?: boolean;
  affected_status?: 'AFFECTED' | 'NOT_AFFECTED';
  clinical_phenotype?: string;
  order_observations?: RequisitionOrderObservation[];
}

/* --------------------------------------------------------------------------
 * Base Entities
 * TODO: Make non-required properties requierd, but nullable
 * -------------------------------------------------------------------------- */
export interface BaseRequisitionOrder {
  // Identifiers
  order_id: string;

  // Tracking
  created_at: string;
  reported_at?: string | null;
  // TODO: Replace with ExternalOrderStatus once verified `OrderStatus.REPORTED` is not used
  status: OrderStatus;

  product_id: string;
  product_name: string;
  product_family_name?: string | null;
  product_test_name?: string | null;
  product_test_type?: string | null;
  product_test_subclass?: string | null;
  results?: ToplineResult[];
}

export interface BaseRequisition<TOrder extends BaseRequisitionOrder = BaseRequisitionOrder> {
  // Identifiers
  external_mrn?: string | null;
  individual_id: string;
  requisition_id: string;

  // Patient
  name: IUsername;
  subject?: RequisitionSubject; // Doesn't seem to exist

  // Tracking
  clinician_review_status?: ClinicianReviewStatus;
  // TODO: Replace with ExternalOrderStatus once verified `OrderStatus.REPORTED` is not used
  external_status?: OrderStatus;
  order_date: string;
  sample_received_at?: string | null;
  submitted_at: string;
  updated_at: string;
  first_reported_at?: string | null;
  clinical_summary_letter_ready_at?: string | null;

  // Relations
  clinical_summary_letter?: ClinicalSummaryLetter | null;
  orders: TOrder[];
}

/* --------------------------------------------------------------------------
 * Requisition Details
 * -------------------------------------------------------------------------- */
export interface CaseRequisition {
  external_status?: OrderStatus;
  individual_id: string;
  name: IUsername;
  orders: Order[];
  requisition_id: string;
  sample_received_at?: string;
  is_primary_requisition?: boolean;

  relationship: 'PARENT' | 'SIBLING';
}

export interface Requisition {
  // Identifiers
  external_mrn?: string;
  individual_id: string;
  requisition_id: string;

  // Patient
  name: IUsername;
  subject?: RequisitionSubject; // Doesn't seem to exist

  // Tracking
  clinician_review_status?: ClinicianReviewStatus;
  external_status?: OrderStatus;
  order_date: string;
  sample_received_at?: string;
  submitted_at: string;
  updated_at: string;
  first_reported_at?: string | null;
  clinical_summary_letter_ready_at?: string | null;

  // Relations
  case_requisitions?: CaseRequisition[];
  clinicians?: Clinician[];
  fvt?: RequisitionFamilyVariantTest;
  orders: Order[];
  family_members?: RequisitionFamilyMember[];

  // Other
  ancestry?: string[];
  billing?: RequisitionBilling;
  campaign_code?: string;
  clinical_summary_letter?: ClinicalSummaryLetter;
  clinic_name?: string;
  consent_grants?: RequisitionConsentGrant[];
  guardian?: {
    name: IUsername;
    relationship_to_patient: string;
  };
  indications?: string;
  order_observations?: RequisitionOrderObservation[];
  post_test_counseling?: string;
  preferred_report_delivery?: string;
  remote_mrn?: string;
  rerequisition_type?: RerequisitionType | null;
  sample_request?: RequisitionSampleRequest;
}

/* --------------------------------------------------------------------------
 * Requisition List
 * -------------------------------------------------------------------------- */
export interface RequisitionListOrder {
  // Identifiers
  order_id: string;

  // Tracking
  created_at: string;
  // TODO: Replace with ExternalOrderStatus once verified `OrderStatus.REPORTED` is not used
  reported_at: string | null;
  status: OrderStatus;

  product_id: string;
  product_name: string;
  product_family_name?: string | null;
  product_test_name?: string | null;
  product_test_type?: string | null;
  product_test_subclass?: string | null;
  results: ToplineResult[];
}

export interface RequisitionListResultItem {
  // Identifiers
  external_mrn: string | null;
  individual_id: string;
  requisition_id: string;

  // Patient
  name: IUsername;

  // Tracking
  clinician_review_status: ClinicianReviewStatus;
  // TODO: Replace with ExternalOrderStatus once verified `OrderStatus.REPORTED` is not used
  external_status: OrderStatus;
  order_date: string;
  sample_received_at: string | null;
  submitted_at: string;
  updated_at: string;

  // Relations
  clinical_summary_letter: ClinicalSummaryLetter | null;
  ordering_provider: Clinician | null;
  orders: RequisitionListOrder[];
  rerequisition_type?: RerequisitionType | null;
}

export type PaginatedRequisitionsResponse = PaginatedApiResponse<RequisitionListResultItem>;

export interface ExtendedRequisitionListResultItem extends RequisitionListResultItem {
  display_status: string | null;
}

/* --------------------------------------------------------------------------
 * Per-requisition notification recipients (sc-47262 / sc-47263)
 * -------------------------------------------------------------------------- */
export type RequisitionNotificationMode = 'DEFAULT' | 'CUSTOM';

export interface RequisitionNotificationRecipient {
  provider_id: string;
  name: string;
  email: string;
  selected: boolean;
}

export interface RequisitionNotificationSettings {
  mode: RequisitionNotificationMode;
  recipients: RequisitionNotificationRecipient[];
}

export interface SaveRequisitionNotificationSettingsPayload {
  mode: RequisitionNotificationMode;
  selected_provider_ids: string[];
}
